import { Suspense, useMemo } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Tab } from '/src/components/headers/tab-header';

import { usePermissions, useSite } from '/src/hooks';

import { FEATURES } from '/src/features/_global/constants';
import { SITE_SETTINGS_TAB, getDefaultSiteSettingsTab } from '/src/features/options/constants';

// eslint-disable-next-line no-restricted-syntax
export default function SiteDetail() {
  const { siteId } = useParams();
  const permissions = usePermissions();

  const canVisitApi = permissions.can('navigate', FEATURES.API).allowed;
  const canVisitTracking = permissions.can('navigate', FEATURES.TRACKING_OPTIONS).allowed;
  const canViewCheckInstall = permissions.can('navigate', FEATURES.INSTALLATION).allowed;
  const canVisitNotifications = permissions.can('navigate', FEATURES.NOTIFICATIONS).allowed;
  const canViewRecordings = permissions.can('view', FEATURES.RECORDINGS).allowed;

  const { sites, loadingSites } = useSite({
    dontSelectSite: true,
  });

  const selectedSite = useMemo(() => {
    if (!sites || sites.length === 0) return null;
    const selectSite = sites.find((site) => site.id === Number(siteId));

    return selectSite;
  }, [sites, siteId]);

  const defaultTab = getDefaultSiteSettingsTab({
    canViewRecordings,
    canVisitTracking,
    canVisitApi,
    canVisitCheckInstall: canViewCheckInstall,
    canVisitNotifications,
  });

  const siteContext = {
    site: selectedSite,
    siteId: selectedSite?.id || Number(siteId),
    siteName: selectedSite?.name,
    loadingSite: loadingSites,
  };

  if (loadingSites) return null;

  if (!selectedSite) {
    return <Navigate to="/options/site-settings" replace />;
  }

  return (
    <>
      <div className="border-b border-mystic-500 bg-white px-10">
        <div className="flex h-[53px] min-w-[830px] items-end">
          {canViewRecordings && (
            <Tab to={`/options/site-settings/${siteId}/${SITE_SETTINGS_TAB.RECORDINGS.path}`}>
              {SITE_SETTINGS_TAB.RECORDINGS.label}
            </Tab>
          )}
          {canVisitTracking && (
            <Tab to={`/options/site-settings/${siteId}/${SITE_SETTINGS_TAB.TRACKING.path}`}>
              {SITE_SETTINGS_TAB.TRACKING.label}
            </Tab>
          )}
          {canVisitApi && (
            <Tab to={`/options/site-settings/${siteId}/${SITE_SETTINGS_TAB.API.path}`}>
              {SITE_SETTINGS_TAB.API.label}
            </Tab>
          )}
          {canViewCheckInstall && (
            <Tab to={`/options/site-settings/${siteId}/${SITE_SETTINGS_TAB.INSTALLATION.path}`}>
              {SITE_SETTINGS_TAB.INSTALLATION.label}
            </Tab>
          )}
          {canVisitNotifications && (
            <Tab to={`/options/site-settings/${siteId}/${SITE_SETTINGS_TAB.NOTIFICATIONS.path}`}>
              {SITE_SETTINGS_TAB.NOTIFICATIONS.label}
            </Tab>
          )}
        </div>
      </div>

      <div className="mx-10 mt-10">
        <Suspense fallback={null}>
          <Outlet context={{ ...siteContext, defaultTab }} />
        </Suspense>
      </div>
    </>
  );
}
